(() => {
  if (window.CHWovenStoryReading) {
    window.CHWovenStoryReading.refresh(document);
    return;
  }

  const roots = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let requestedFrame;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => value * value * (3 - 2 * value);

  const prepareText = (target, tokens) => {
    if (target.dataset.chWovenReadingPrepared === 'true') {
      target.querySelectorAll('.ch-woven-reading-char').forEach((character) => {
        tokens.push({ type: 'char', node: character });
      });
      return;
    }

    const text = target.textContent || '';
    const fragment = document.createDocumentFragment();

    text.split(/(\s+)/).forEach((piece) => {
      if (!piece) return;
      if (/^\s+$/.test(piece)) {
        fragment.append(document.createTextNode(piece));
        return;
      }

      const word = document.createElement('span');
      word.className = 'ch-woven-reading-word';

      [...piece].forEach((character) => {
        const span = document.createElement('span');
        span.className = 'ch-woven-reading-char';
        span.textContent = character;
        span.setAttribute('aria-hidden', 'true');
        word.append(span);
        tokens.push({ type: 'char', node: span });
      });

      fragment.append(word);
    });

    target.textContent = '';
    target.setAttribute('aria-label', text.trim());
    target.append(fragment);
    target.dataset.chWovenReadingPrepared = 'true';
  };

  const measureLines = (root) => {
    const controller = root._chWovenReadingController;
    if (!controller) return;

    const charTokens = controller.tokens.filter(({ type }) => type === 'char');
    const lineTops = [];
    const characterRects = charTokens.map(({ node }) => node.getBoundingClientRect());

    characterRects.forEach(({ top }) => {
      if (!lineTops.some((lineTop) => Math.abs(lineTop - top) < 3)) lineTops.push(top);
    });

    lineTops.sort((a, b) => a - b);
    if (!lineTops.length) return;

    const tokenRects = controller.tokens.map((token) => ({
      token,
      rect: token.node.getBoundingClientRect(),
    }));

    const lines = lineTops.map((top, index) => ({ index, top, tokens: [] }));

    tokenRects.forEach(({ token, rect }) => {
      const line = lines.reduce((nearest, candidate) =>
        Math.abs(candidate.top - rect.top) < Math.abs(nearest.top - rect.top) ? candidate : nearest
      );

      token.lineIndex = line.index;
      line.tokens.push({ token, rect });
    });

    lines.forEach((line) => {
      if (!line.tokens.length) return;

      const lineLeft = Math.min(...line.tokens.map(({ rect }) => rect.left));
      const lineRight = Math.max(...line.tokens.map(({ rect }) => rect.right));
      const lineWidth = Math.max(lineRight - lineLeft, 1);

      line.tokens.forEach(({ token, rect }) => {
        const center = rect.left + rect.width * 0.5;
        token.horizontalPosition = clamp((center - lineLeft) / lineWidth);
      });
    });

    controller.lineCount = lineTops.length;
  };

  const configureReading = (root) => {
    const controller = root._chWovenReadingController;
    if (!controller) return;

    const runway = root.closest('[data-ch-woven-reading-runway]');
    controller.scrollPerLine = clamp(
      Number.parseFloat(runway?.dataset.chWovenReadingLineDistance) || 90,
      50,
      180
    );
    measureLines(root);
  };

  const prepare = (root) => {
    if (root._chWovenReadingController) {
      configureReading(root);
      return;
    }

    const flow = root.querySelector('.ch-woven-story__flow');
    if (!flow) return;

    const tokens = [];

    [...flow.children].forEach((piece) => {
      if (piece.matches('.ch-woven-text-piece')) {
        prepareText(piece, tokens);
      } else if (piece.matches('.ch-woven-image-piece')) {
        tokens.push({ type: 'image', node: piece });
      }
    });

    root._chWovenReadingController = { tokens };
    root.dataset.chWovenReadingReady = 'true';
    roots.add(root);
    configureReading(root);
  };

  const render = (root) => {
    const controller = root._chWovenReadingController;
    if (!controller) return;

    const initialOpacity = clamp(
      Number.parseFloat(getComputedStyle(root).getPropertyValue('--ch-woven-reading-opacity')) || 0.2
    );

    if (reducedMotion.matches) {
      controller.tokens.forEach(({ type, node }) => {
        if (type === 'char') node.style.opacity = '1';
        if (type === 'image') node.dataset.chWovenImageActive = 'true';
      });
      return;
    }

    const triggerPosition = window.innerHeight * 0.72;
    const readingPosition = clamp(
      (triggerPosition - root.getBoundingClientRect().top) / controller.scrollPerLine,
      0,
      controller.lineCount
    );
    const softEdge = 0.14;

    controller.tokens.forEach((token) => {
      const lineProgress = readingPosition - token.lineIndex;

      if (token.type === 'char') {
        const characterProgress = clamp(
          (lineProgress - token.horizontalPosition + softEdge) / softEdge
        );
        const opacity = initialOpacity + (1 - initialOpacity) * smoothstep(characterProgress);
        token.node.style.opacity = opacity.toFixed(3);
      } else {
        token.node.dataset.chWovenImageActive =
          lineProgress >= token.horizontalPosition - 0.04 ? 'true' : 'false';
      }
    });
  };

  const update = () => {
    requestedFrame = undefined;

    roots.forEach((root) => {
      if (!root.isConnected) {
        roots.delete(root);
        return;
      }

      render(root);
    });
  };

  const requestUpdate = () => {
    if (requestedFrame) return;
    requestedFrame = window.requestAnimationFrame(update);
  };

  const refresh = (scope) => {
    if (scope.matches?.('[data-ch-woven-reading]')) prepare(scope);
    scope.querySelectorAll?.('[data-ch-woven-reading]').forEach(prepare);
    requestUpdate();
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      roots.forEach(configureReading);
      requestUpdate();
    },
    { passive: true }
  );
  reducedMotion.addEventListener('change', requestUpdate);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));

  window.CHWovenStoryReading = { refresh };
  refresh(document);
  document.fonts?.ready.then(() => {
    roots.forEach(configureReading);
    requestUpdate();
  });
})();
